import React from 'react';
import { connect } from 'react-redux';
import { setLocale } from '../../actions/intl';
import {
  t, PHONE, PHONE_LABEL, initReveal, Figure,
  IconPhone, IconTick,
  Header, Footer, CallBar
} from './SiteChrome';

/*
 * Halaman About — port dari site/about.html.
 *
 * Angka trust strip sama seperti home: dibiarkan literal di JSX (hanya labelnya
 * lewat copy.json), karena angka Latin tidak berubah antar bahasa. Semuanya
 * masih klaim yang BELUM dikonfirmasi client — lihat catatan di copy.json.
 */

const PROMISES = [1, 2, 3, 4, 5, 6];
const DOING = [1, 2, 3];

const LD_ABOUT = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  url: 'https://basrecovery.com/about',
  mainEntity: {
    '@type': 'AutoRepair',
    name: 'Bas Recovery',
    url: 'https://basrecovery.com/',
    telephone: PHONE,
    foundingDate: '2024',
    address: { '@type': 'PostalAddress', addressCountry: 'AE', addressRegion: 'Dubai' },
    numberOfEmployees: { '@type': 'QuantitativeValue', value: 54 },
    slogan: 'Nobody should feel stranded on the road.'
  }
};

class About extends React.Component {
  state = { navOpen: false };

  toggleNav = () => this.setState(s => ({ navOpen: !s.navOpen }));
  closeNav = () => this.setState({ navOpen: false });

  switchLang = () => {
    const next = this.props.currentLocale === 'ar' ? 'en-US' : 'ar';
    document.documentElement.lang = next;
    this.props.setLocale({ locale: next });
  };

  componentDidMount() {
    this.onKey = e => { if (e.key === 'Escape') this.closeNav(); };
    document.addEventListener('keydown', this.onKey);
    this.io = initReveal();
  }
  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKey);
    if (this.io) this.io.disconnect();
  }

  render() {
    const { navOpen } = this.state;
    const L = this.props.currentLocale || 'en-US';
    const tx = (k, v) => t(L, k, v);

    return (
      <div>
        <a className="skip" href="#main">{tx('nav.skip')}</a>

        <Header
          locale={L} current="/about"
          navOpen={navOpen}
          onToggleNav={this.toggleNav}
          onCloseNav={this.closeNav}
          onSwitchLang={this.switchLang}
        />

        <main id="main">
          <section className="hero" style={{ paddingBlock: 'clamp(40px,6vw,68px) clamp(44px,6vw,72px)' }}>
            <div className="wrap">
              <p className="eyebrow">{tx('abt.eyebrow')}</p>
              <h1 style={{ fontSize: 'var(--fs-h1)', maxWidth: '24ch' }}>{tx('abt.h1')}</h1>
              <p className="lead" style={{ maxWidth: '58ch' }}>{tx('abt.lead')}</p>
            </div>
          </section>

          {/* ── APA YANG SEBENARNYA KAMI KERJAKAN ── */}
          <section>
            <div className="wrap split">
              <div>
                <h2>{tx('abt.do.h2')}</h2>
                <p>{tx('abt.do.p1')}</p>
                <p style={{ marginTop: 'var(--sp-4)' }}>{tx('abt.do.p2')}</p>
                <div className="ticks">
                  {DOING.map(n => (
                    <div className="tick" key={n}>
                      <IconTick /><span><strong>{tx(`abt.do.${n}b`)}</strong>{tx(`abt.do.${n}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <Figure photo="team" alt={tx('abt.figureAlt')} caption={tx('abt.figure')} />
            </div>
          </section>

          {/* ── ANGKA 30 HARI TERAKHIR ── */}
          <section className="band-warm">
            <div className="wrap">
              <div className="sec-head">
                <h2>{tx('abt.today.h2')}</h2>
                <p className="lead">{tx('abt.today.lead')}</p>
              </div>
              <div className="trust-grid">
                <div className="trust-item"><div className="trust-num">12,400<em>+</em></div><div className="trust-lbl">{tx('trust.jobs')}</div></div>
                <div className="trust-item"><div className="trust-num">54</div><div className="trust-lbl">{tx('abt.trust.partners')}</div></div>
                <div className="trust-item"><div className="trust-num">4.8<em>/5</em></div><div className="trust-lbl">{tx('trust.rating')}</div></div>
                <div className="trust-item"><div className="trust-num">25<em>{tx('abt.trust.medianUnit')}</em></div><div className="trust-lbl">{tx('abt.trust.median')}</div></div>
              </div>
            </div>
          </section>

          {/* ── JANJI KAMI ── */}
          <section>
            <div className="wrap">
              <div className="sec-head"><h2>{tx('abt.values.h2')}</h2></div>
              <div className="svc-grid">
                {PROMISES.map(n => (
                  <div className="svc" key={n}>
                    <h3>{tx(`abt.v${n}.title`)}</h3>
                    <p>{tx(`abt.v${n}.body`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── CTA ── */}
          <section className="band-night">
            <div className="wrap" style={{ textAlign: 'center' }}>
              <h2 style={{ maxWidth: '22ch', marginInline: 'auto' }}>{tx('abt.cta.h2')}</h2>
              <p style={{ maxWidth: '52ch', margin: 'var(--sp-4) auto var(--sp-6)' }}>{tx('abt.cta.lead')}</p>
              <div style={{ display: 'flex', gap: 'var(--sp-3)', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a className="btn btn-call btn-lg" href={`tel:${PHONE}`}>
                  <IconPhone />{tx('hero.call', { phone: PHONE_LABEL })}
                </a>
                <a className="btn btn-ghost btn-lg" href="/contact">{tx('abt.cta.contact')}</a>
              </div>
            </div>
          </section>
        </main>

        <Footer locale={L} />
        <CallBar locale={L} />

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(LD_ABOUT) }} />
      </div>
    );
  }
}

const mapState = state => ({ currentLocale: state?.intl?.locale });
const mapDispatch = { setLocale };

export default connect(mapState, mapDispatch)(About);
